import React from "react";

import { SxProps, styled, Box, Typography, Stack, AccordionSummary, AccordionDetails, Divider } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PanelHeader, { PanelHeaderProps } from "../PanelHeader/panel-header";
import SmallAccordion from "../../Misc/SmallAccordion";
import Panel from "../Panel/panel";
import { PlatformLanguageCode } from "../../../Core/Api/Types/platform-language-code";
import { Language } from "../../../Core/Api/Types/language";
import { useTranslation } from "react-i18next";

export interface SynopsisPanelProps {
  sx?: SxProps;
  width?: number;
  open: boolean;
  onClose?: React.MouseEventHandler;
  list: { languageCode: PlatformLanguageCode | null; languageLocalized: Language | null; text: string }[];
}

const StyledAccordion = styled(SmallAccordion)(({ theme }) => ({
  background: "none",
  ".MuiAccordionDetails-root": {
    border: "none",
  },
}));

const SynopsisPanel: React.FC<SynopsisPanelProps> = (props) => {
  const { t } = useTranslation();
  const getLanguage = (item: any) => {
    if (item.languageLocalized)
      return {
        value: item.languageLocalized?.isoCodeLocalized,
        label: null,
        flag: item.languageLocalized?.englishName,
      };
    else return { value: item.languageCode?.label, label: item.languageCode?.value, flag: "" }; //Non-localized version
  };

  return (
    <Panel
      header={<PanelHeader title={t("common:synopsis")} onClose={props.onClose as PanelHeaderProps["onClose"]} />}
      anchor="right"
      open={props.open}
      onClose={props.onClose}
    >
      <Box>
        {props.list?.map((item, index) => (
          <Box key={index}>
            <StyledAccordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "text.tier" }} />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <Stack alignItems="center" direction="row" spacing={1}>
                  <Typography variant="h5" textTransform="uppercase" component="span" color="text.tier">
                    {getLanguage(item).value}
                    {getLanguage(item).label ? ` (${getLanguage(item).label})` : ""}
                  </Typography>
                  <Typography variant="h6" textTransform="uppercase" component="h3" color="text.tier">
                    {getLanguage(item).flag}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 3, pb: 3 }}>
                <Typography
                  variant="subtitle2"
                  component="p"
                  color="text.secondary"
                  dangerouslySetInnerHTML={{ __html: item.text }}
                />
              </AccordionDetails>
            </StyledAccordion>
            <Divider />
          </Box>
        ))}
      </Box>
    </Panel>
  );
};

export default SynopsisPanel;
